'use client';

import * as React from 'react';
import type { ReactNode } from 'react';

/* `'error'` added in v5 as an alias for `'danger'` to align with the
   kit-wide tint vocabulary (Badge/Empty/MetricDisplay all use 'error').
   Both work through v5; v6 may deprecate 'danger' via codemod. */
export type ToastTone = 'info' | 'success' | 'warning' | 'danger' | 'error';

export interface ToastProps {
  children?: ReactNode;
  tone?: ToastTone;
  onDismiss?: () => void;
  duration?: number;
}

export const Toast = React.forwardRef<HTMLDivElement, ToastProps>(function Toast(
  { children, tone = 'info', onDismiss, duration = 2200 },
  ref,
) {
  /* Stash onDismiss in a ref so the auto-dismiss timer doesn't reset
     whenever the parent re-renders with a fresh inline arrow. The
     timer starts once per duration value and fires with the latest
     onDismiss at dismiss time. */
  const onDismissRef = React.useRef(onDismiss);
  React.useEffect(() => {
    onDismissRef.current = onDismiss;
  });

  React.useEffect(() => {
    /* Guard non-finite durations: Infinity / NaN / negative falls
       through to setTimeout, which most browsers clamp to ~1 ms
       and then fire — instant dismiss, opposite of the intent.
       Treat !Number.isFinite(duration) as "stay open until
       the consumer dismisses manually" (the same shape as
       duration === 0). */
    if (!duration || !Number.isFinite(duration) || duration <= 0) return;
    const t = setTimeout(() => {
      onDismissRef.current?.();
    }, duration);
    return () => clearTimeout(t);
  }, [duration]);

  /* Match the ARIA live-region shape to the tone:
     - danger / error / warning → role="alert" (implicit aria-live
       "assertive"): something the user must see NOW (sync failed,
       payment declined).
     - info / success           → role="status" (implicit aria-live
       "polite"): informational, wait for the user to finish what
       they're doing. */
  const role = tone === 'danger' || tone === 'error' || tone === 'warning' ? 'alert' : 'status';

  /* Normalize the deprecated 'error' alias to 'danger' for the
     data-tone attribute so the existing CSS selectors keep working
     without duplicating every `[data-tone='danger']` rule. */
  const dataTone = tone === 'error' ? 'danger' : tone;

  return (
    <div ref={ref} className="j-toast" data-tone={dataTone} role={role}>
      <span className="j-toast-dot" />
      {children}
    </div>
  );
});
